import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalReports: number;
  avgWaitTime: number;
  activeCrossings: number;
  reportsWithPhotos: number;
}

interface RealtimeEvent {
  type: 'new_user' | 'new_checkin' | 'new_checkout' | 'user_updated';
  data: any;
  timestamp: string;
}

interface ChartDataPoint {
  name: string;
  value: number;
  timestamp: string;
}

export const useAdminRealtimeUpdates = (isSpanish: boolean = false) => {
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalReports: 0,
    avgWaitTime: 0,
    activeCrossings: 5,
    reportsWithPhotos: 0
  });
  
  const [recentEvents, setRecentEvents] = useState<RealtimeEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userChartData, setUserChartData] = useState<ChartDataPoint[]>([]);
  const [checkinChartData, setCheckinChartData] = useState<ChartDataPoint[]>([]);

  // Fetch initial data
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch users
      const { data: usersData } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      // Fetch checkins
      const { data: checkinsData } = await supabase
        .from('checkins')
        .select('*')
        .order('created_at', { ascending: false });

      // Calculate stats
      const activeUsersCount = (usersData || []).filter(u => u.status === 'active').length;
      const photosCount = (checkinsData || []).filter(c => c.photo_uploaded).length;
      
      // Calculate average wait time
      const completedCheckins = (checkinsData || []).filter(c => c.check_out_time);
      const avgWait = completedCheckins.length > 0
        ? completedCheckins.reduce((acc, c) => {
            const waitMinutes = Math.floor(
              (new Date(c.check_out_time).getTime() - new Date(c.created_at).getTime()) / (1000 * 60)
            );
            return acc + waitMinutes;
          }, 0) / completedCheckins.length
        : 0;

      setStats({
        totalUsers: (usersData || []).length,
        activeUsers: activeUsersCount,
        totalReports: (checkinsData || []).length,
        avgWaitTime: Math.round(avgWait),
        activeCrossings: 5,
        reportsWithPhotos: photosCount
      });

      // Generate chart data for last 7 days
      generateChartData(usersData || [], checkinsData || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching initial admin data:', error);
      setLoading(false);
    }
  };

  // Generate chart data from historical data
  const generateChartData = (users: any[], checkins: any[]) => {
    const now = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (6 - i));
      return date;
    });

    // User registrations per day
    const usersByDay = last7Days.map(date => {
      const dayStart = new Date(date.setHours(0, 0, 0, 0));
      const dayEnd = new Date(date.setHours(23, 59, 59, 999));
      
      const count = users.filter(u => {
        const created = new Date(u.created_at);
        return created >= dayStart && created <= dayEnd;
      }).length;

      return {
        name: date.toLocaleDateString('en-US', { weekday: 'short' }),
        value: count,
        timestamp: date.toISOString()
      };
    });

    // Check-ins per day
    const checkinsByDay = last7Days.map(date => {
      const dayStart = new Date(date.setHours(0, 0, 0, 0));
      const dayEnd = new Date(date.setHours(23, 59, 59, 999));
      
      const count = checkins.filter(c => {
        const created = new Date(c.created_at);
        return created >= dayStart && created <= dayEnd;
      }).length;

      return {
        name: date.toLocaleDateString('en-US', { weekday: 'short' }),
        value: count,
        timestamp: date.toISOString()
      };
    });

    setUserChartData(usersByDay);
    setCheckinChartData(checkinsByDay);
  };

  // Update chart data when new events arrive
  const updateChartData = (eventType: 'user' | 'checkin') => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'short' });
    
    if (eventType === 'user') {
      setUserChartData(prev => 
        prev.map(point => 
          point.name === today 
            ? { ...point, value: point.value + 1 }
            : point
        )
      );
    } else {
      setCheckinChartData(prev => 
        prev.map(point => 
          point.name === today 
            ? { ...point, value: point.value + 1 }
            : point
        )
      );
    }
  };

  useEffect(() => {
    fetchInitialData();

    // Subscribe to users table
    const usersChannel = supabase
      .channel('admin-users-changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'users'
      }, (payload) => {
        console.log('New user registered:', payload.new);
        
        setStats(prev => ({
          ...prev,
          totalUsers: prev.totalUsers + 1,
          activeUsers: payload.new.status === 'active' ? prev.activeUsers + 1 : prev.activeUsers
        }));

        updateChartData('user');

        const event: RealtimeEvent = {
          type: 'new_user',
          data: payload.new,
          timestamp: new Date().toISOString()
        };
        
        setRecentEvents(prev => [event, ...prev.slice(0, 49)]);
        
        toast.success(
          isSpanish ? '🎉 Nuevo usuario registrado' : '🎉 New user registered',
          {
            description: payload.new.full_name || payload.new.email
          }
        );
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'users'
      }, (payload) => {
        console.log('User updated:', payload.new);
        
        // Recalculate active users
        fetchInitialData();

        const event: RealtimeEvent = {
          type: 'user_updated',
          data: payload.new,
          timestamp: new Date().toISOString()
        };
        
        setRecentEvents(prev => [event, ...prev.slice(0, 49)]);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Subscribed to users table');
          setIsConnected(true);
        }
      });

    // Subscribe to checkins table
    const checkinsChannel = supabase
      .channel('admin-checkins-changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'checkins'
      }, (payload) => {
        console.log('New check-in:', payload.new);
        
        setStats(prev => ({
          ...prev,
          totalReports: prev.totalReports + 1,
          reportsWithPhotos: payload.new.photo_uploaded 
            ? prev.reportsWithPhotos + 1 
            : prev.reportsWithPhotos
        }));

        updateChartData('checkin');

        const event: RealtimeEvent = {
          type: 'new_checkin',
          data: payload.new,
          timestamp: new Date().toISOString()
        };
        
        setRecentEvents(prev => [event, ...prev.slice(0, 49)]);
        
        toast.info(
          isSpanish ? '📍 Nuevo check-in' : '📍 New check-in',
          {
            description: `${payload.new.garita} - ${payload.new.lane_type || 'normal'}`
          }
        );
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'checkins'
      }, (payload) => {
        // Check if this is a checkout (check_out_time was just added)
        if (payload.new.check_out_time) {
          console.log('New checkout:', payload.new);
          
          // Recalculate average wait time
          fetchInitialData();

          const event: RealtimeEvent = {
            type: 'new_checkout',
            data: payload.new,
            timestamp: new Date().toISOString()
          };
          
          setRecentEvents(prev => [event, ...prev.slice(0, 49)]);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Subscribed to checkins table');
        }
      });

    return () => {
      supabase.removeChannel(usersChannel);
      supabase.removeChannel(checkinsChannel);
    };
  }, [isSpanish]);

  const clearEvents = () => {
    setRecentEvents([]);
  };

  const refreshData = () => {
    fetchInitialData();
  };

  return {
    stats,
    recentEvents,
    isConnected,
    loading,
    userChartData,
    checkinChartData,
    clearEvents,
    refreshData
  };
};
